import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import PageHeader from "components/PageLayout";
import EmptyState from "components/EmptyState";
import Skeleton from "components/Skeleton";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import { useFunctions } from "hooks/useFunctions";
import UserPickerField from "components/Functions/UserPickerField";
import { Briefcase, Plus, Pencil, X, Users as UsersIcon } from "lucide-react";

function slugify(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function FunctionsRegistry() {
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const authUser = { id: user.id, accessToken: user.accessToken };

  const { functions, loading, refetch } = useFunctions({
    include: "holders",
    includeInactive: true,
  });

  // Optimistic override for the inline active toggle (functionKey -> bool).
  const [activeOverride, setActiveOverride] = useState({});
  const isActive = (f) =>
    activeOverride[f.functionKey] !== undefined ? activeOverride[f.functionKey] : f.active;

  const [modal, setModal] = useState(null); // null | { mode: "create"|"edit", fn? }

  const rows = useMemo(
    () =>
      [...functions].sort((a, b) => {
        if ((b.active ? 1 : 0) !== (a.active ? 1 : 0)) return (b.active ? 1 : 0) - (a.active ? 1 : 0);
        return (a.label || "").localeCompare(b.label || "");
      }),
    [functions]
  );

  const toggleActive = async (fn) => {
    const prev = isActive(fn);
    const next = !prev;
    setActiveOverride((o) => ({ ...o, [fn.functionKey]: next }));
    try {
      await makeApiRequest("put", apiRoutes.updateFunction(fn.functionKey), authUser, companyId, {
        active: next,
      });
      toast({ status: "success", title: next ? "Function activated" : "Function deactivated" });
      await refetch();
      setActiveOverride((o) => {
        const n = { ...o };
        delete n[fn.functionKey];
        return n;
      });
    } catch (err) {
      setActiveOverride((o) => ({ ...o, [fn.functionKey]: prev }));
      toast({
        status: "destructive",
        title: "Failed to update function",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Job Functions"
        subtitle="The ops-queue vocabulary (Underwriting, Risk, …). Assign functions to Sound team members on the Team page; set each function's default owner here."
        actions={
          <button className="btn btn-primary" onClick={() => setModal({ mode: "create" })}>
            <Plus className="h-4 w-4" />
            New function
          </button>
        }
      />

      <div className="container py-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-2/3" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            heading="No job functions yet"
            description="Create your first function to start routing ticket work."
            action={
              <button className="btn btn-primary" onClick={() => setModal({ mode: "create" })}>
                <Plus className="h-4 w-4" />
                New function
              </button>
            }
          />
        ) : (
          <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["Function", "Status", "Holders", "Assignment", "Default owner", ""].map((c) => (
                    <th
                      key={c}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((f) => {
                  const active = isActive(f);
                  return (
                    <tr
                      key={f.functionKey}
                      className="cursor-pointer transition-colors hover:bg-muted"
                      onClick={() => setModal({ mode: "edit", fn: f })}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
                            <Briefcase className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-foreground">{f.label}</div>
                            <div className="truncate font-mono text-xs text-muted-foreground">
                              {f.functionKey}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => toggleActive(f)}
                          className={`badge ${active ? "badge-success" : "badge-muted"} transition-colors`}
                          title={active ? "Active — click to deactivate" : "Inactive — click to activate"}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${active ? "bg-current" : "bg-muted-foreground"}`}
                          />
                          {active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <UsersIcon className="h-3.5 w-3.5" />
                          {f.holderCount ?? (f.holders ? f.holders.length : 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`badge ${f.strategy === "leastLoaded" ? "badge-info" : "badge-muted"}`}
                          title={
                            f.strategy === "leastLoaded"
                              ? "Capacity-based — routes to the least-loaded holder (default owner is the fallback)"
                              : "Fixed owner — auto-assigns to the default owner"
                          }
                        >
                          {f.strategy === "leastLoaded" ? "Capacity-based" : "Fixed owner"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {f.defaultOwner ? (
                          f.defaultOwner.displayName || f.defaultOwner.email
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <FunctionModal
          mode={modal.mode}
          fn={modal.fn}
          authUser={authUser}
          companyId={companyId}
          existingKeys={functions.map((f) => f.functionKey)}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function FunctionModal({ mode, fn, authUser, companyId, existingKeys, onClose, onSaved }) {
  const { toast } = useToast();
  const isEdit = mode === "edit";

  const [label, setLabel] = useState(fn?.label || "");
  const [functionKey, setFunctionKey] = useState(fn?.functionKey || "");
  const [keyEdited, setKeyEdited] = useState(isEdit);
  const [active, setActive] = useState(fn?.active ?? true);
  const [strategy, setStrategy] = useState(fn?.strategy || "defaultOwner");
  const [owner, setOwner] = useState({
    id: fn?.defaultOwnerId || "",
    label: fn?.defaultOwner?.displayName || fn?.defaultOwner?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onLabelChange = (v) => {
    setLabel(v);
    if (!isEdit && !keyEdited) setFunctionKey(slugify(v));
  };

  const canSubmit =
    !!label.trim() && (isEdit || !!functionKey.trim()) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    const key = slugify(functionKey);
    if (!isEdit) {
      if (!key) {
        toast({ status: "warning", title: "A function key is required" });
        return;
      }
      if (existingKeys.includes(key)) {
        toast({ status: "warning", title: "That function key already exists" });
        return;
      }
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await makeApiRequest("put", apiRoutes.updateFunction(fn.functionKey), authUser, companyId, {
          label: label.trim(),
          active,
          defaultOwnerId: owner.id || null,
          strategy,
        });
      } else {
        await makeApiRequest("post", apiRoutes.createFunction, authUser, companyId, {
          functionKey: key,
          label: label.trim(),
          defaultOwnerId: owner.id || undefined,
          strategy,
        });
      }
      toast({ status: "success", title: isEdit ? "Function updated" : "Function created" });
      onSaved();
    } catch (err) {
      toast({
        status: "destructive",
        title: isEdit ? "Failed to update function" : "Failed to create function",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="surface w-full max-w-md rounded-xl p-6 shadow-lg ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
              <Briefcase className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{isEdit ? "Edit function" : "New function"}</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {isEdit ? "Update the label, status, or default owner." : "Create an ops-queue function."}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost h-8 w-8 shrink-0 p-0" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">Label</span>
            <input
              autoFocus
              type="text"
              className="input"
              placeholder="e.g. Underwriting"
              value={label}
              onChange={(e) => onLabelChange(e.target.value)}
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">
              Function key{" "}
              <span className="font-normal text-muted-foreground">
                {isEdit ? "(immutable)" : "(lowercase snake_case)"}
              </span>
            </span>
            <input
              type="text"
              className="input font-mono disabled:opacity-60"
              placeholder="underwriting"
              value={functionKey}
              disabled={isEdit}
              onChange={(e) => {
                setKeyEdited(true);
                setFunctionKey(e.target.value);
              }}
              onBlur={() => !isEdit && setFunctionKey((k) => slugify(k))}
            />
          </label>

          <div className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">Default owner</span>
            <UserPickerField
              value={owner.id}
              valueLabel={owner.label}
              onChange={({ userId, displayName }) => setOwner({ id: userId, label: displayName })}
            />
            <span className="mt-1 text-xs text-muted-foreground">
              Templated tickets for this function auto-assign to the default owner (else queue as unassigned).
            </span>
          </div>

          <div className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">Assignment strategy</span>
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {[
                { value: "defaultOwner", label: "Fixed owner" },
                { value: "leastLoaded", label: "Capacity-based" },
              ].map((opt, i) => {
                const on = strategy === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStrategy(opt.value)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
                      on ? "bg-brand/[0.14] text-foreground" : "bg-card text-muted-foreground hover:text-foreground"
                    } ${i > 0 ? "border-l border-border" : ""}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <span className="mt-1 text-xs text-muted-foreground">
              {strategy === "leastLoaded"
                ? "New tickets route to the least-loaded holder of this function; the default owner is the fallback."
                : "New tickets auto-assign to the default owner."}
            </span>
          </div>

          {isEdit && (
            <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <span className="font-medium text-foreground">Active</span>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            </label>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {isEdit ? "Save changes" : "Create function"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default requireAuth(FunctionsRegistry);
